import { Loader } from '@/modules/base/classes/loaders'
import { Result } from '@/modules/errorHandling/results'
import { SourceStreamCaster, StreamStatusCode } from '@/types/streams'
import { KeyDict } from '../../../types'
import { SystemStatus } from '../../../types/ops'
import { CastK360Info } from '@/modules/casts/types'
import firebase from 'firebase/app'

export enum CefRendererType {
  Room = 'room',
  Background = 'background',
  Bumper = 'bumper',
  Extern = 'extern',
  Stream = 'stream'
}

export enum CefAppUserType {
  Producer = 'producer',
  Moderator = 'moderator',
  Master = 'master'
}

export interface CefRendererParams {
  assetUrl?: string
  assetUrlShort?: string
}

export interface CefApplianceAddParams {
  name: string,
  startDate: Date,
  endDate: Date,
  previewRegion: string,
  previewRegionBackup?: string
  mixerRegion: string,
  mixerRegionBackup?: string,
  station: string|null,
  liveClipping: boolean
}

export interface CefRenderer {
  buffersize: number,
  outputs: KeyDict<CefOutput>,
  outputs_backup?: KeyDict<CefOutput>,
  status: SystemStatus,
  status_backup?: SystemStatus,
  uri: string,
  castId: string|null,
  streamId?: string|null, // TODO: remove optional and refactor
  type: CefRendererType,
  params?: CefRendererParams
}

export enum CefOutputType {
  Broadcast = 'broadcast',
  Preview = 'preview'
}

export interface CefOutput {
  status: SystemStatus,
  type: CefOutputType,
  uri: string
}

export class CefAppliance extends CastK360Info {
  cefrenderers: KeyDict<CefRenderer>
  deleted: boolean
  healthcheck_watchdog: number
  healthcheck_watchdog_backup: number|null
  inputselection: string
  inputpreload: string|null
  machineid: string|null
  machineid_backup: string|null
  mixer_status: SystemStatus
  mixer_status_backup: SystemStatus|null
  online_sessions: KeyDict<string[]> = {}
  name: string
  outputs: KeyDict<CefOutput>
  outputs_backup: KeyDict<CefOutput>
  region: string
  region_backup: string|null
  source_streams: KeyDict<Partial<SourceStreamCaster>>
  station: {
    slate: string,
    slateShort: string
  }
  switchDelay: number
  team_id: string
  version: string
  longStingerSwitch: number
  shortStingerSwitch: number

  override get numTracks () { return 1 }

  // @ts-ignore   // once typescript is updated to 4.6, this can be gone -> cannot call anything before super()
  private constructor (data: unknown) {
    const machineid = Loader.loadStringOrNull(data, 'machineid', null)
    // @ts-ignore
    super({ ...(data ?? {}), k360_event_id: Number.parseInt(machineid) })
    this.machineid = machineid
    this.name = Loader.loadString(data, 'name')
    this.cefrenderers = Loader.loadAndCast<KeyDict<CefRenderer>>(data, 'cefrenderers', {})
    this.deleted = Loader.loadBoolean(data, 'deleted', false)
    this.healthcheck_watchdog = Loader.loadNumber(data, 'healthcheck_watchdog', 0)
    this.healthcheck_watchdog_backup = Loader.loadNumberOrNull(data, 'healthcheck_watchdog_backup', null)
    this.inputselection = Loader.loadString(data, 'inputselection', '')
    this.inputpreload = Loader.loadStringOrNull(data, 'inputpreload', null)
    this.machineid_backup = Loader.loadStringOrNull(data, 'machineid_backup', null)
    this.mixer_status = Loader.loadString(data, 'mixer_status', SystemStatus.NONE) as SystemStatus
    this.mixer_status_backup = Loader.loadStringOrNull(data, 'mixer_status_backup',
                                                       SystemStatus.NONE) as SystemStatus|null
    this.outputs = Loader.loadAndCast<KeyDict<CefOutput>>(data, 'outputs', {})
    this.outputs_backup = Loader.loadAndCast<KeyDict<CefOutput>>(data, 'outputs_backup', {})
    this.region = Loader.loadString(data, 'region')
    this.region_backup = Loader.loadStringOrNull(data, 'region_backup', null)
    this.source_streams = Loader.loadAndCast<KeyDict<Partial<SourceStreamCaster>>>(data, 'source_streams', {})
    this.station = this.loadStation(data)
    this.switchDelay = Loader.loadNumber(data, 'switchDelay', 0)
    this.team_id = Loader.loadString(data, 'team_id')
    this.version = Loader.loadString(data, 'version')
    this.longStingerSwitch = Loader.loadNumber(data, 'longStingerSwitch', 8000)
    this.shortStingerSwitch = Loader.loadNumber(data, 'shortStingerSwitch', 1200)
    this.online_sessions = Loader.loadAndCast<KeyDict<string[]>>(data, 'online_sessions', {})
  }

  private loadStation (data: unknown): { slate: string, slateShort: string } {
    return {
      // @ts-ignore
      slate: Loader.loadString(data.station, 'slate', ''),
      // @ts-ignore
      slateShort: Loader.loadString(data.station, 'slateShort', '')
    }
  }

  static load (data: Partial<CefAppliance>): Result<CefAppliance, 'invalid param'|'unknown'> {
    try {
      return Result.success(new CefAppliance(data))
    } catch (error) {
      if (error instanceof Error) {
        return Result.fail('invalid param', error.message)
      }
      return Result.fail('unknown', `${error}`)
    }
  }
}

export interface StatusDotWithLabelInfo {
  label: string,
  status: StreamStatusCode,
  tooltip?: string,
  tooltipTitle?: string
}

export interface CefApplianceStatusInfo {
  labelOutput: string,
  labelK360: string,
  titleK360: string,
  statusOutput: StreamStatusCode,
  statusOutput2: StreamStatusCode,
  statusK360: StreamStatusCode,
  k360Id: string
}

export enum CefApplianceCastStatusChangeType {
  Assigned = 'assigned',
  Cleared = 'cleared',
  Closed = 'closed',  // TODO: Should be assigned when Room is stopped(/deleted) or it automatically expires (backend)
  Created = 'created',
  Live = 'live',
  Moved = 'moved'
}

export interface CefApplianceCastStatusChange {
  status: CefApplianceCastStatusChangeType,
  timestamp: Date,
  cefApplianceId: string|null,
  teamId: string
}
export interface CefApplianceCastStatus {
  statusChanges: CefApplianceCastStatusChange[] | firebase.firestore.FieldValue
}
